/**
 * Inventory / character-sheet overlay.
 *
 * Layout:
 *  Left column: paper-doll with all 9 equipment slots for the selected
 *               party member; click an equipped slot to unequip.
 *  Middle:      party-member tabs (click to switch).
 *  Right:       inventory grid; click an item to equip it on the selected
 *               member's matching slot (swaps in if occupied).
 *
 * State callbacks:
 *  - onEquip(memberIdx, itemId) → returns true if equipped
 *  - onUnequip(memberIdx, slot) → returns the unequipped itemId (or null)
 *  - getState() → returns the latest snapshot to re-render after a change
 */

import { ITEMS, RARITY_TEXT, type EquipSlot } from '../items';

export interface PartyMemberView {
  role: string;
  name: string;
  alive: boolean;
  equipment: Partial<Record<EquipSlot, string>>;
  effectiveStats: { maxHp: number; hp: number; damage: number; range: number; speed: number; armor: number };
}

export interface InventoryState {
  party: PartyMemberView[];
  inventory: string[]; // item IDs
}

export interface InventoryCallbacks {
  onEquip(memberIdx: number, itemId: string): void;
  onUnequip(memberIdx: number, slot: EquipSlot): void;
  onClose(): void;
  getState(): InventoryState;
}

const SLOT_ORDER: EquipSlot[] = ['helm', 'amulet', 'weapon', 'chest', 'gloves', 'ring1', 'ring2', 'legs', 'boots'];
const SLOT_LABEL: Record<EquipSlot, string> = {
  helm: 'Helm', amulet: 'Amulet', weapon: 'Weapon', chest: 'Chest', gloves: 'Gloves',
  ring1: 'Ring I', ring2: 'Ring II', legs: 'Legs', boots: 'Boots',
};

let activeRoot: HTMLElement | null = null;
let selectedMember = 0;

export function showInventory(cb: InventoryCallbacks): void {
  hideInventory();
  selectedMember = 0;
  const root = document.createElement('div');
  root.className = 'wb-overlay wb-inv';
  document.body.appendChild(root);
  activeRoot = root;
  render(cb);
}

export function hideInventory(): void {
  if (activeRoot) {
    activeRoot.remove();
    activeRoot = null;
  }
}

export function isInventoryOpen(): boolean { return !!activeRoot; }

function render(cb: InventoryCallbacks): void {
  if (!activeRoot) return;
  const state = cb.getState();
  if (selectedMember >= state.party.length) selectedMember = 0;
  const member = state.party[selectedMember];
  activeRoot.innerHTML = `
    <div class="wb-overlay-bg"></div>
    <div class="wb-inv-panel">
      <div class="wb-inv-header">
        <h1>Inventory · Character Sheet</h1>
        <button class="wb-btn wb-inv-close">Close [I]</button>
      </div>
      <div class="wb-inv-tabs">
        ${state.party.map((p, i) => `
          <button class="wb-inv-tab ${i === selectedMember ? 'active' : ''} ${p.alive ? '' : 'dead'}" data-idx="${i}">
            ${esc(p.name)}${p.alive ? '' : ' ✕'}
          </button>`).join('')}
      </div>
      <div class="wb-inv-body">
        <div class="wb-inv-paperdoll">
          ${SLOT_ORDER.map(slot => slotCell(slot, member)).join('')}
        </div>
        <div class="wb-inv-stats">
          <div class="wb-inv-stats-title">${esc(member.name)} · Effective</div>
          <dl>
            <div><dt>HP</dt><dd>${member.effectiveStats.hp} / ${member.effectiveStats.maxHp}</dd></div>
            <div><dt>Damage</dt><dd>${member.effectiveStats.damage}</dd></div>
            <div><dt>Range</dt><dd>${member.effectiveStats.range.toFixed(1)}</dd></div>
            <div><dt>Speed</dt><dd>${member.effectiveStats.speed.toFixed(1)}</dd></div>
            <div><dt>Armor</dt><dd>${member.effectiveStats.armor}</dd></div>
          </dl>
        </div>
        <div class="wb-inv-grid-wrap">
          <div class="wb-inv-stats-title">Bag · ${state.inventory.length} items</div>
          <div class="wb-inv-grid">
            ${state.inventory.length === 0
              ? `<div class="wb-inv-empty">No items. Defeat enemies and walk over their drops.</div>`
              : state.inventory.map((id, i) => inventoryCell(id, i)).join('')}
          </div>
        </div>
      </div>
    </div>
  `;

  // Wire interactions.
  activeRoot.querySelector('.wb-inv-close')!.addEventListener('click', () => { cb.onClose(); hideInventory(); });
  activeRoot.querySelectorAll<HTMLElement>('.wb-inv-tab').forEach(el => {
    el.addEventListener('click', () => {
      selectedMember = Number(el.dataset.idx);
      render(cb);
    });
  });
  activeRoot.querySelectorAll<HTMLElement>('.wb-inv-slot').forEach(el => {
    el.addEventListener('click', () => {
      const slot = el.dataset.slot as EquipSlot;
      cb.onUnequip(selectedMember, slot);
      render(cb);
    });
  });
  activeRoot.querySelectorAll<HTMLElement>('.wb-inv-item').forEach(el => {
    el.addEventListener('click', () => {
      const itemId = el.dataset.id!;
      cb.onEquip(selectedMember, itemId);
      render(cb);
    });
  });
}

function slotCell(slot: EquipSlot, m: PartyMemberView): string {
  const itemId = m.equipment[slot];
  const item = itemId ? ITEMS[itemId] : null;
  if (!item) {
    return `<div class="wb-inv-slot empty" data-slot="${slot}">
      <span class="wb-inv-slot-label">${SLOT_LABEL[slot]}</span>
    </div>`;
  }
  return `<div class="wb-inv-slot filled" data-slot="${slot}" title="Click to unequip">
    <span class="wb-inv-slot-label">${SLOT_LABEL[slot]}</span>
    <span class="wb-inv-slot-name" style="color: ${RARITY_TEXT[item.rarity]}">${esc(item.name)}</span>
    <span class="wb-inv-slot-mods">${modSummary(item.mods)}</span>
  </div>`;
}

function inventoryCell(itemId: string, idx: number): string {
  const item = ITEMS[itemId];
  if (!item) return '';
  return `<div class="wb-inv-item" data-id="${itemId}" data-idx="${idx}" title="Click to equip">
    <span class="wb-inv-item-name" style="color: ${RARITY_TEXT[item.rarity]}">${esc(item.name)}</span>
    <span class="wb-inv-item-slot">${SLOT_LABEL[item.slot]}</span>
    <span class="wb-inv-item-mods">${modSummary(item.mods)}</span>
  </div>`;
}

function modSummary(mods: object): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(mods as Record<string, number>)) {
    if (v === undefined || v === 0) continue;
    const sign = v > 0 ? '+' : '';
    parts.push(`${sign}${v} ${k}`);
  }
  return parts.join(' · ') || '—';
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

// Styles — appended once.
const STYLE_ID = 'wb-inv-styles';
if (!document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    .wb-inv-panel {
      position: relative; z-index: 1;
      min-width: 820px; max-width: 920px;
      max-height: 86vh;
      padding: 20px 26px;
      background: #12100c; border: 1px solid #3a3327; border-radius: 8px;
      color: #f4ecd6; font-family: -apple-system, system-ui, sans-serif;
      box-shadow: 0 18px 50px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,220,160,0.08);
      overflow: hidden; display: flex; flex-direction: column;
    }
    .wb-inv-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
    .wb-inv-header h1 { margin: 0; font-size: 18px; color: #ffd54a; letter-spacing: 0.02em; }
    .wb-inv-tabs { display: flex; gap: 4px; margin-bottom: 14px; border-bottom: 1px solid #3a3327; }
    .wb-inv-tab {
      padding: 7px 14px; background: transparent; border: 1px solid transparent;
      border-bottom: none; color: #c8bfa3; cursor: pointer;
      font-family: monospace; font-size: 12px;
      border-top-left-radius: 4px; border-top-right-radius: 4px;
    }
    .wb-inv-tab:hover { color: #f4ecd6; background: rgba(255,213,74,0.06); }
    .wb-inv-tab.active { color: #ffd54a; border-color: #3a3327; background: #1a1610; }
    .wb-inv-tab.dead { opacity: 0.45; text-decoration: line-through; }
    .wb-inv-body {
      display: grid; grid-template-columns: 1.1fr 0.9fr 1.3fr; gap: 16px;
      flex: 1; overflow: hidden;
    }
    .wb-inv-paperdoll {
      display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 6px;
      align-content: start;
    }
    .wb-inv-slot {
      display: flex; flex-direction: column; padding: 6px;
      background: #0f0e0c; border: 1px solid #3a3327; border-radius: 3px;
      min-height: 60px; font-size: 10px; cursor: pointer;
      transition: background 140ms, border-color 140ms;
    }
    .wb-inv-slot:hover { background: #1a1610; border-color: #6a5a3a; }
    .wb-inv-slot.empty { color: #5a4f3a; }
    .wb-inv-slot-label { font-family: monospace; font-size: 9px; color: #9a8f7a; text-transform: uppercase; letter-spacing: 0.06em; }
    .wb-inv-slot-name { font-size: 11px; font-weight: 600; margin-top: 4px; }
    .wb-inv-slot-mods { font-family: monospace; font-size: 9px; color: #c8bfa3; margin-top: 3px; }
    .wb-inv-stats { padding: 10px 12px; background: #0f0e0c; border: 1px solid #3a3327; border-radius: 4px; }
    .wb-inv-stats-title { font-family: monospace; font-size: 11px; color: #ffd54a; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 10px; }
    .wb-inv-stats dl { margin: 0; padding: 0; }
    .wb-inv-stats dl > div { display: flex; justify-content: space-between; padding: 3px 0; font-size: 12px; }
    .wb-inv-stats dt { color: #9a8f7a; margin: 0; }
    .wb-inv-stats dd { color: #f4ecd6; margin: 0; font-variant-numeric: tabular-nums; }
    .wb-inv-grid-wrap { display: flex; flex-direction: column; overflow: hidden; }
    .wb-inv-grid { overflow-y: auto; flex: 1; display: grid; grid-template-columns: 1fr; gap: 4px; padding-right: 6px; }
    .wb-inv-empty { padding: 30px 8px; color: #9a8f7a; text-align: center; font-size: 12px; font-style: italic; }
    .wb-inv-item {
      display: flex; flex-direction: column; padding: 6px 9px;
      background: #1a1610; border: 1px solid #3a3327; border-radius: 3px;
      cursor: pointer; transition: background 140ms, border-color 140ms;
    }
    .wb-inv-item:hover { background: #2a2418; border-color: #ffd54a; }
    .wb-inv-item-name { font-size: 12px; font-weight: 600; }
    .wb-inv-item-slot { font-family: monospace; font-size: 9px; color: #9a8f7a; letter-spacing: 0.05em; text-transform: uppercase; }
    .wb-inv-item-mods { font-family: monospace; font-size: 10px; color: #c8bfa3; margin-top: 3px; }
  `;
  document.head.appendChild(style);
}
